import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import { getBusinessDetails, updateBusinessDetails } from 'services/businessService';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

const BusinessDetailsTab = ({selectedBusiness }) => {
  //const selectedBusiness = useSelector((state)=> state?.business.selectedBusiness);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    contactNo:'',
    secondaryContactNo: '',
    email: '',
    websiteUrl: '',
    logoUrl:'',
    yearEstablished:'',
  });

  const [logoPreview, setLogoPreview] = useState(formData?.logoUrl);
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoadingBusinessDetails, setIsLoadingBusinessDetails] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleLogoUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newLogoUrl = e?.target?.result;
        setLogoPreview(newLogoUrl);
        handleInputChange('logo', newLogoUrl);
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleSave = async() => {
    if(!hasChanges){
      toast.info("There are no changes.");
      return;
    }

    setIsLoadingBusinessDetails(true);
    const results = await updateBusinessDetails(selectedBusiness.businessId, formData);

    if(results.success){
      toast.success(results.message|| 'Business details updated successfully.');
      setHasChanges(false);
    }
    else{
      toast.error(results.message || 'Failed to update business details');
    }
    setIsLoadingBusinessDetails(false);
  };

  const handleReset = async () => {
    await loadBusinessDetails();
  };

  const loadBusinessDetails = async(showToast=true)=>{
    if(selectedBusiness.businessId == null)
      return;

    setIsLoadingBusinessDetails(true);
    const results = await getBusinessDetails(selectedBusiness.businessId);

    if(results.success){
      setFormData(results.data);
      setDataFetched(true);
      setHasChanges(false);

      if(showToast)
        toast.success(results.message)
    }
    else{
      toast.error(results?.message || 'Failed to retrieve business details');
    }
    setIsLoadingBusinessDetails(false);

  }

  useEffect(()=>{
    if(dataFetched)
      return;

    loadBusinessDetails(false);
  },[]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Business Details</h3>
          <p className="text-sm text-muted-foreground">
            Update your business information and branding
          </p>
        </div>
        {hasChanges && (
          <div className="flex items-center space-x-2">
            <div className="status-indicator warning"></div>
            <span className="text-sm text-warning">Unsaved changes</span>
          </div>
        )}
      </div>
      {/* Logo Section */}
      {/* <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Business Logo</h4>
        <div className="flex items-start space-x-6">
          <div className="flex-shrink-0">
            <div className="w-24 h-24 border-2 border-dashed border-border rounded-lg overflow-hidden bg-muted flex items-center justify-center">
              {logoPreview ? (
                <Image
                  src={logoPreview}
                  alt="Business Logo"
                  className="w-full h-full object-cover"
                />
              ) : (
                <Icon name="Building2" size={32} className="text-muted-foreground" />
              )}
            </div>
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Upload Logo
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                id="logo-upload"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('logo-upload')?.click()}
                iconName="Upload"
                iconPosition="left"
                iconSize={16}
              >
                Choose File
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Recommended: Square image, minimum 200x200px, max 2MB
            </p>
          </div>
        </div>
      </div> */}
      {/* Business Information Form */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Business Information</h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="lg:col-span-2">
            <Input
              label="Business Name"
              type="text"
              placeholder="Enter business name"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              required
            />
          </div>

          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-foreground mb-2">
              Business Description
            </label>
            <textarea
              className="w-full min-h-[120px] px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-vertical"
              placeholder="Describe your business services and specialties"
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              rows={5}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Provide a detailed description of your services and expertise
            </p>
          </div>

          <Input
            label="Primary Phone"
            type="text"
            placeholder="Enter primary phone"
            value={formData?.contactNo}
            onChange={(e) => handleInputChange('contactNo', e?.target?.value)}
            required
          />

          <Input
            label="Secondary Phone"
            type="text"
            placeholder="Enter secondary phone"
            value={formData?.secondaryContactNo}
            onChange={(e) => handleInputChange('secondaryContactNo', e?.target?.value)}
            
          />

          <Input
            label="Email Address"
            type="email"
            placeholder="Enter email address"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            required
          />

          <Input
            label="Website URL"
            type="text"
            placeholder="Enter website url"
            value={formData?.websiteUrl}
            onChange={(e) => handleInputChange('websiteUrl', e?.target?.value)}
            
          />

          <Input
            label="Year Established"
            type="number"
            placeholder="Enter year established"
            value={formData?.yearEstablished}
            onChange={(e) => handleInputChange('yearEstablished', e?.target?.value)}
            min="1900"
            max={new Date()?.getFullYear()}
          />
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <Button
          variant="outline"
          onClick={handleReset}
          disabled={!hasChanges || isLoadingBusinessDetails}
          iconName="RotateCcw"
          iconPosition="left"
          iconSize={16}
        >
          Reset Changes
        </Button>

        <div className="flex items-center space-x-3">
          {/* <div className="flex items-center space-x-2">
            <div className="status-indicator active"></div>
            <span className="text-sm text-muted-foreground">Auto-save enabled</span>
          </div> */}
          <Button
            variant="default"
            onClick={handleSave}
            disabled={!hasChanges}
            loading={isLoadingBusinessDetails}
            iconName="Save"
            iconPosition="left"
            iconSize={16}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BusinessDetailsTab;